/**
 * ContextDrawer — Right contextual panel for Workspace Shell V1
 * 
 * Slides in from right to show:
 * - Article details
 * - LitScholar AI tools (with "Expand to Full Page" option)
 * - Discussion threads (links to Community)
 * - Audio generation
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  FileText,
  Brain,
  MessageSquare,
  Headphones,
  ExternalLink,
  BookmarkPlus,
  X,
  Maximize2,
  Play,
  Users,
  Loader2,
  BookOpen,
} from 'lucide-react';

interface Article {
  pmid: string;
  title: string;
  abstract?: string;
  journal?: string;
  authors?: string;
  pub_date?: string;
  ai_summary?: string;
  design_tags?: string[];
}

interface ContextDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  article?: Article | null;
  onSaveToLibrary?: (pmid: string) => void;
  onGenerateAudio?: (pmid: string) => void;
  onOpenLitScholar?: (pmid: string) => void;
  generatingAudio?: boolean;
}

export const ContextDrawer: React.FC<ContextDrawerProps> = ({
  open,
  onOpenChange,
  article,
  onSaveToLibrary,
  onGenerateAudio,
  onOpenLitScholar,
  generatingAudio = false,
}) => {
  const navigate = useNavigate();
  
  if (!article) return null;

  // Navigate to full-page LitScholar with article context
  const handleExpandLitScholar = () => {
    onOpenChange(false); // Close drawer
    navigate(`/litscholar?pmid=${article.pmid}`);
  };

  // Navigate to community discussion for this article
  const handleViewDiscussions = () => {
    onOpenChange(false);
    navigate(`/community?article=${article.pmid}`);
  };

  // Navigate to audio digests for this article
  const handleViewAudioHistory = () => {
    onOpenChange(false);
    navigate(`/audio-digests?article=${article.pmid}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[420px] p-0 flex flex-col"
        data-testid="context-drawer"
      >
        <SheetHeader className="p-4 border-b border-[hsl(var(--border))]">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <SheetTitle 
                className="responsive-title text-base font-semibold leading-tight line-clamp-2"
                data-testid="context-article-title"
              >
                {article.title}
              </SheetTitle>
              <SheetDescription className="mt-1 text-sm">
                {article.journal && (
                  <span className="text-[hsl(var(--primary))]">{article.journal}</span>
                )}
                {article.pub_date && (
                  <span className="text-[hsl(var(--muted-foreground))] ml-2">
                    {new Date(article.pub_date).toLocaleDateString()}
                  </span>
                )}
              </SheetDescription>
            </div>
          </div>
          
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mt-3">
            {onSaveToLibrary && (
              <Button
                size="sm"
                variant="default"
                onClick={() => onSaveToLibrary(article.pmid)}
                data-testid="context-save-button"
              >
                <BookmarkPlus className="w-4 h-4 mr-1" />
                Save to LitHub
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank')}
              data-testid="context-pubmed-button"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              PubMed
            </Button>
          </div>
        </SheetHeader>

        <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
          <TabsList className="px-4 pt-2 justify-start bg-transparent border-b border-[hsl(var(--border))] rounded-none">
            <TabsTrigger value="details" className="gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              Details
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-1.5">
              <Brain className="w-3.5 h-3.5" />
              LitScholar
            </TabsTrigger>
            <TabsTrigger value="audio" className="gap-1.5">
              <Headphones className="w-3.5 h-3.5" />
              Audio
            </TabsTrigger>
            <TabsTrigger value="discuss" className="gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              Discuss
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <TabsContent value="details" className="p-4 mt-0 space-y-4">
              {/* Design tags */}
              {article.design_tags && article.design_tags.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                    Study Design
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {article.design_tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {article.ai_summary && (
                <div>
                  <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                    AI Summary
                  </h4>
                  <p className="text-sm text-[hsl(var(--foreground))] leading-relaxed">
                    {article.ai_summary}
                  </p>
                </div>
              )}

              {/* Abstract */}
              {article.abstract && (
                <div>
                  <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                    Abstract
                  </h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                    {article.abstract}
                  </p>
                </div>
              )}

              {/* Authors */}
              {article.authors && (
                <div>
                  <h4 className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-2">
                    Authors
                  </h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {article.authors}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="ai" className="p-4 mt-0">
              <div className="space-y-4">
                <div className="text-center py-4">
                  <Brain className="w-12 h-12 text-[hsl(var(--primary))] mx-auto mb-4" />
                  <h3 className="font-medium mb-2">LitScholar AI</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                    Get AI-powered insights about this article
                  </p>
                </div>

                <div className="space-y-2">
                  {onOpenLitScholar && (
                    <Button 
                      onClick={() => onOpenLitScholar(article.pmid)}
                      className="w-full"
                      data-testid="litscholar-quick-btn"
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      Quick Analysis
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline"
                    onClick={handleExpandLitScholar}
                    className="w-full"
                    data-testid="litscholar-expand-btn"
                  >
                    <Maximize2 className="w-4 h-4 mr-2" />
                    Expand to Full Page
                  </Button>
                </div>

                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">
                  Full page mode provides advanced AI workspace features
                </p>
              </div>
            </TabsContent>

            <TabsContent value="audio" className="p-4 mt-0">
              <div className="space-y-4">
                <div className="text-center py-4">
                  <Headphones className="w-12 h-12 text-[hsl(var(--primary))] mx-auto mb-4" />
                  <h3 className="font-medium mb-2">Audio Summary</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                    Listen to an AI-narrated summary of this article
                  </p>
                </div>

                <div className="space-y-2">
                  {onGenerateAudio && (
                    <Button 
                      onClick={() => onGenerateAudio(article.pmid)}
                      disabled={generatingAudio}
                      className="w-full"
                      data-testid="audio-generate-btn"
                    >
                      {generatingAudio ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Generate Audio
                        </>
                      )}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline"
                    onClick={handleViewAudioHistory}
                    className="w-full"
                    data-testid="audio-history-btn"
                  >
                    <Headphones className="w-4 h-4 mr-2" />
                    View Audio History
                  </Button>
                </div>

                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">
                  Audio digests are saved to your history
                </p>
              </div>
            </TabsContent>

            <TabsContent value="discuss" className="p-4 mt-0">
              <div className="space-y-4">
                <div className="text-center py-4">
                  <MessageSquare className="w-12 h-12 text-[hsl(var(--primary))] mx-auto mb-4" />
                  <h3 className="font-medium mb-2">Discuss</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                    Start or join a discussion about this article in LitClub
                  </p>
                </div>

                <div className="space-y-2">
                  <Button 
                    onClick={handleViewDiscussions}
                    className="w-full"
                    data-testid="discuss-view-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    View Discussions
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      navigate('/community');
                    }}
                    className="w-full"
                    data-testid="discuss-browse-btn"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Browse LitClub
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      navigate('/digests/manage');
                    }}
                    className="w-full"
                    data-testid="discuss-manage-digests-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Manage Digests
                  </Button>
                </div>

                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">
                  Join specialty rooms and discuss with peers
                </p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default ContextDrawer;
